import React, { Fragment, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { CreativeTypes } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import { Box, Button, Flex, Form, Input, List, ModalBase } from '@bynder/design-system';
import { IconArrowLeft, IconSearch } from '@bynder/icons';
import { PartialComponent } from 'packages/services/ComponentService';
import useDebouncedState from 'packages/hooks/useDebouncedState';
import {
    CARD_MIN_SIZE,
    ListItemContent,
    ModalContentWrapper,
    ScrollableListContainer,
    StyledCardGrid,
    StyledModalContainer,
} from './styled';
import ComponentCard from './ComponentCard';
import CollectionMenu from './CollectionMenu';
import EditableCollectionName from './EditableCollectionName';
import { useComponentsContext } from '../ComponentsContext';
import useComponentDeleteModal from '../ComponentDeleteModal';
import { EnrichedComponentCollection } from './types';
import { useCollectionEditModal } from '../CollectionEditModal';
import { initialState, reducer } from './reducer';
import { fetchCollections, fetchComponents } from './api';
import DelayedLoader from './DelayedLoader';
import OverviewFilters, { getActiveTypes, loadFilters, TypeFilters } from './OverviewFilters';

interface LoadingHandle {
    cleanup: () => void;
    finalize: () => void;
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onApply: (component: PartialComponent, isUniqueContentProperties: boolean) => void;
    isImageDesign?: boolean;
    registerComponentSaveCallback: (callback: (resetCollection: boolean) => void) => void;
}

const ComponentOverview = ({
    isOpen,
    onClose,
    onApply,
    isImageDesign = false,
    registerComponentSaveCallback,
}: Props) => {
    const { translate } = useTranslate();
    const [state, dispatch] = useReducer(reducer, initialState);
    const [filters, setFilters] = useState<TypeFilters>(() => loadFilters(isImageDesign));

    const loadingRef = useRef<LoadingHandle | null>(null);
    const stateRef = useRef(state);
    stateRef.current = state;
    const debouncedSearchRef = useRef('');
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const componentOverviewContainerRef = useRef(null);
    const observerCollections = useRef<IntersectionObserver | undefined>(undefined);
    const observerComponents = useRef<IntersectionObserver | undefined>(undefined);

    const loadCollections = useCallback((page: number, search: string, types?: CreativeTypes[]): LoadingHandle => {
        let active = true;
        dispatch({ type: 'FETCH_COLLECTIONS_START' });

        const handle: LoadingHandle = {
            cleanup: () => {
                active = false;
            },
            finalize: () => {
                if (active) {
                    dispatch({ type: 'FETCH_DONE', payload: { target: 'collections' } });
                }
            },
        };

        fetchCollections(page, search, types)
            .then((result) => {
                if (result && active) {
                    dispatch({
                        type: 'FETCH_COLLECTIONS_SUCCESS',
                        payload: { collections: result.collections, hasMore: result.hasMore, page },
                    });
                }
            })
            .catch(() => {
                // swallow — the UI stays in loading state until cleanup/finalize
            })
            .finally(() => {
                handle.finalize();
            });

        return handle;
    }, []);

    const loadComponents = useCallback(
        ({
            page,
            search,
            collectionId,
            types,
        }: {
            page: number;
            search: string;
            collectionId?: number;
            types?: CreativeTypes[];
        }): LoadingHandle => {
            let active = true;
            dispatch({ type: 'FETCH_COMPONENTS_START' });

            const handle: LoadingHandle = {
                cleanup: () => {
                    active = false;
                },
                finalize: () => {
                    if (active) {
                        dispatch({ type: 'FETCH_DONE', payload: { target: 'components' } });
                    }
                },
            };

            fetchComponents({ page, search, collectionId, types })
                .then((result) => {
                    if (result && active) {
                        dispatch({
                            type: 'FETCH_COMPONENTS_SUCCESS',
                            payload: {
                                components: result.components,
                                total: result.total,
                                hasMore: result.hasMore,
                                page,
                            },
                        });
                    }
                })
                .catch(() => {
                    // swallow — the UI stays in loading state until cleanup/finalize
                })
                .finally(() => {
                    handle.finalize();
                });

            return handle;
        },
        [],
    );

    const {
        state: searchValue,
        debouncedState: debouncedSearchValue,
        setState: setSearchValue,
    } = useDebouncedState('', 500, (value: string) => {
        const { selectedCollection, collections } = stateRef.current;
        const types = getActiveTypes(filtersRef.current);

        // Skip redundant load: when value is empty and collections are already loaded,
        // the useEffect already handled the initial fetch.
        if (!value && !selectedCollection && collections.length > 0) {
            return;
        }

        loadingRef.current?.cleanup();

        if (selectedCollection) {
            loadingRef.current = loadComponents({ page: 0, search: value, collectionId: selectedCollection.id, types });
        } else if (value) {
            loadingRef.current = loadComponents({ page: 0, search: value, types });
        } else {
            loadingRef.current = loadCollections(0, value, types);
        }
    });

    debouncedSearchRef.current = debouncedSearchValue;

    const refetchData = useCallback(
        (resetCollection: boolean) => {
            dispatch({ type: 'REFETCH', payload: { resetCollection } });
            loadingRef.current?.cleanup();
            const selected = resetCollection ? null : stateRef.current.selectedCollection;
            const search = debouncedSearchRef.current;
            const types = getActiveTypes(filtersRef.current);

            if (selected) {
                loadingRef.current = loadComponents({ page: 0, search, collectionId: selected.id, types });
            } else if (search) {
                loadingRef.current = loadComponents({ page: 0, search, types });
            } else {
                loadingRef.current = loadCollections(0, search, types);
            }
        },
        [loadCollections, loadComponents],
    );

    const { openComponentModal } = useComponentsContext();

    // Register callback for component save success
    useEffect(() => {
        registerComponentSaveCallback(refetchData);
    }, [registerComponentSaveCallback, refetchData]);

    const { renderDeleteModal, onDeleteModalOpen } = useComponentDeleteModal({
        onDeleteSuccess: refetchData,
    });

    const { renderCollectionEditModal, openCollectionEditModal } = useCollectionEditModal({
        onSaveSuccess: (name) => {
            dispatch({ type: 'UPDATE_SELECTED_COLLECTION_NAME', payload: { name } });
        },
    });

    const onComponentEdit = (component: PartialComponent) => {
        openComponentModal('edit', [], component, state.selectedCollection ? state.selectedCollection.id : undefined);
    };

    const onCollectionEdit = (collection: EnrichedComponentCollection) => {
        openCollectionEditModal(collection);
    };

    const onCollectionClick = (collection: EnrichedComponentCollection) => {
        if (clickTimerRef.current) {
            return;
        }

        clickTimerRef.current = setTimeout(() => {
            dispatch({ type: 'SELECT_COLLECTION', payload: collection });
            loadingRef.current?.cleanup();
            loadingRef.current = loadComponents({
                page: 0,
                search: debouncedSearchRef.current,
                collectionId: collection.id,
                types: getActiveTypes(filtersRef.current),
            });
            clickTimerRef.current = null;
        }, 250);
    };

    const onCollectionDoubleClick = (collection: EnrichedComponentCollection) => {
        if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
        }
    };

    const handleCollectionUpdate = (id: number, name: string) => {
        dispatch({ type: 'UPDATE_COLLECTION_NAME', payload: { id, name } });
    };

    const lastCollectionElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (state.isCollectionsLoading) {
                return;
            }

            if (observerCollections.current) {
                observerCollections.current.disconnect();
            }

            observerCollections.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && stateRef.current.hasMoreCollections) {
                    const nextPage = stateRef.current.collectionsPage + 1;
                    dispatch({ type: 'INCREMENT_COLLECTIONS_PAGE' });
                    loadingRef.current?.cleanup();
                    loadingRef.current = loadCollections(
                        nextPage,
                        debouncedSearchRef.current,
                        getActiveTypes(filtersRef.current),
                    );
                }
            });

            if (node) {
                observerCollections.current.observe(node);
            }
        },
        [state.isCollectionsLoading, loadCollections],
    );

    const lastComponentElementRef = useCallback(
        (node: HTMLDivElement) => {
            if (state.isComponentsLoading) {
                return;
            }

            if (observerComponents.current) {
                observerComponents.current.disconnect();
            }

            observerComponents.current = new IntersectionObserver((entries) => {
                if (entries[0].isIntersecting && stateRef.current.hasMoreComponents) {
                    const nextPage = stateRef.current.componentsPage + 1;
                    dispatch({ type: 'INCREMENT_COMPONENTS_PAGE' });
                    loadingRef.current?.cleanup();
                    loadingRef.current = loadComponents({
                        page: nextPage,
                        search: debouncedSearchRef.current,
                        collectionId: stateRef.current.selectedCollection?.id,
                        types: getActiveTypes(filtersRef.current),
                    });
                }
            });

            if (node) {
                observerComponents.current.observe(node);
            }
        },
        [state.isComponentsLoading, loadComponents],
    );

    useEffect(() => {
        if (isOpen) {
            if (searchValue !== '') {
                setSearchValue('');
            }

            dispatch({ type: 'RESET' });
            loadingRef.current?.cleanup();
            loadingRef.current = loadCollections(0, '', getActiveTypes(filtersRef.current));
        }

        return () => {
            loadingRef.current?.cleanup();
            loadingRef.current = null;
        };
    }, [isOpen, loadCollections, setSearchValue]);

    const handleFiltersChange = useCallback(
        (newFilters: TypeFilters) => {
            setFilters(newFilters);
            dispatch({ type: 'REFETCH', payload: { resetCollection: !stateRef.current.selectedCollection } });
            loadingRef.current?.cleanup();
            const selected = stateRef.current.selectedCollection;
            const search = debouncedSearchRef.current;
            const types = getActiveTypes(newFilters);

            if (selected) {
                loadingRef.current = loadComponents({ page: 0, search, collectionId: selected.id, types });
            } else if (search) {
                loadingRef.current = loadComponents({ page: 0, search, types });
            } else {
                loadingRef.current = loadCollections(0, search, types);
            }
        },
        [loadCollections, loadComponents],
    );

    const hasNoResults =
        state.collections.length === 0 &&
        !state.isCollectionsLoading &&
        state.components.length === 0 &&
        !state.isComponentsLoading &&
        searchValue === debouncedSearchValue;

    if (!isOpen) {
        return null;
    }

    return (
        <>
            <StyledModalContainer ref={componentOverviewContainerRef} />
            <ModalBase isOpen={isOpen} onClose={onClose} container={componentOverviewContainerRef.current}>
                <ModalBase.Header title={translate('components.overview.title')} />
                <ModalBase.Content>
                    <ModalContentWrapper>
                        <Box paddingInline="6" paddingBottom="4">
                            <Form onSubmit={(e) => e.preventDefault()}>
                                <Form.Group>
                                    <Form.InputRow>
                                        <Input
                                            value={searchValue}
                                            onChange={setSearchValue}
                                            type="text"
                                            placeholder={translate('components.overview.search.input.placeholder')}
                                            aria-label={translate('components.overview.search.input.label')}
                                            icon={<IconSearch />}
                                            hasClearButton
                                        />
                                        <OverviewFilters
                                            filters={filters}
                                            onChange={handleFiltersChange}
                                            isImageDesign={isImageDesign}
                                        />
                                    </Form.InputRow>
                                </Form.Group>
                            </Form>
                        </Box>
                        {/* key here is used to reset scroll position when search value changes */}
                        <ScrollableListContainer key={debouncedSearchValue}>
                            <Box paddingInline="6">
                                <List>
                                    {!state.selectedCollection ? (
                                        <>
                                            {hasNoResults && (
                                                <p>
                                                    {searchValue.length > 0
                                                        ? translate('components.overview.components.noResults')
                                                        : translate('components.overview.collections.noResults')}
                                                </p>
                                            )}
                                            {debouncedSearchValue.length > 0 && state.components.length > 0 && (
                                                <Box paddingBottom="4">
                                                    <List.Item subtext=" ">
                                                        <span style={{ width: '100%', display: 'inline-block' }}>
                                                            {translate('components.overview.components.results', {
                                                                count: state.componentsTotal,
                                                            })}
                                                        </span>
                                                    </List.Item>
                                                    <StyledCardGrid columnMinWidth={CARD_MIN_SIZE}>
                                                        {state.components.map((component) => (
                                                            <ComponentCard
                                                                key={component.id}
                                                                component={component}
                                                                collectionName={component.collectionName}
                                                                onClick={(isUniqueContentProperties) => {
                                                                    onApply(component, isUniqueContentProperties);
                                                                }}
                                                                onEdit={() => onComponentEdit(component)}
                                                                onDelete={() =>
                                                                    onDeleteModalOpen(component.id, 'component')
                                                                }
                                                                isDisabled={isImageDesign && component.type === 'VIDEO'}
                                                            />
                                                        ))}
                                                    </StyledCardGrid>
                                                    <div
                                                        ref={lastComponentElementRef}
                                                        style={{ height: 1, width: '100%' }}
                                                    />
                                                </Box>
                                            )}
                                            {!debouncedSearchValue.length &&
                                                state.collections.map((collection) => (
                                                    <Fragment key={collection.id}>
                                                        <Box paddingLeft="4" paddingRight="4">
                                                            <EditableCollectionName
                                                                collection={collection}
                                                                onClick={onCollectionClick}
                                                                onDoubleClick={onCollectionDoubleClick}
                                                                onUpdate={handleCollectionUpdate}
                                                                subtext={translate(
                                                                    'components.overview.collections.number',
                                                                    {
                                                                        count: collection.totalComponents,
                                                                    },
                                                                )}
                                                            />
                                                        </Box>
                                                        <Box paddingTop="4" paddingBottom="4">
                                                            <StyledCardGrid columnMinWidth={CARD_MIN_SIZE}>
                                                                {collection.components.map((component) => (
                                                                    <ComponentCard
                                                                        key={component.id}
                                                                        component={component}
                                                                        collectionName={collection.name}
                                                                        onClick={(isUniqueContentProperties) => {
                                                                            onApply(
                                                                                component,
                                                                                isUniqueContentProperties,
                                                                            );
                                                                        }}
                                                                        onEdit={() => onComponentEdit(component)}
                                                                        onDelete={() =>
                                                                            onDeleteModalOpen(component.id, 'component')
                                                                        }
                                                                        isDisabled={
                                                                            isImageDesign && component.type === 'VIDEO'
                                                                        }
                                                                    />
                                                                ))}
                                                            </StyledCardGrid>
                                                        </Box>
                                                    </Fragment>
                                                ))}
                                            <DelayedLoader isLoading={state.isCollectionsLoading}>
                                                <Flex justifyContent="center">
                                                    <Button
                                                        isLoading
                                                        variant="clean"
                                                        aria-label={translate('pages.loading')}
                                                    />
                                                </Flex>
                                            </DelayedLoader>
                                            {state.collections.length > 0 && (
                                                <div
                                                    ref={lastCollectionElementRef}
                                                    style={{ height: 1, width: '100%' }}
                                                />
                                            )}
                                        </>
                                    ) : (
                                        <>
                                            <Box paddingLeft="4" paddingRight="4">
                                                <ListItemContent
                                                    key={state.selectedCollection.id}
                                                    subtext={translate('components.overview.collections.number', {
                                                        count: state.selectedCollection.totalComponents,
                                                    })}
                                                    onClick={() => {
                                                        dispatch({ type: 'DESELECT_COLLECTION' });
                                                        loadingRef.current?.cleanup();
                                                        const search = debouncedSearchRef.current;
                                                        const types = getActiveTypes(filtersRef.current);

                                                        if (search) {
                                                            loadingRef.current = loadComponents({
                                                                page: 0,
                                                                search,
                                                                types,
                                                            });
                                                        } else {
                                                            loadingRef.current = loadCollections(0, '', types);
                                                        }
                                                    }}
                                                    thumbnail={<IconArrowLeft />}
                                                    rightElements={
                                                        <CollectionMenu
                                                            onEdit={() => onCollectionEdit(state.selectedCollection!)}
                                                            onDelete={() =>
                                                                onDeleteModalOpen(
                                                                    state.selectedCollection!.id,
                                                                    'collection',
                                                                )
                                                            }
                                                        />
                                                    }
                                                >
                                                    {state.selectedCollection.name}
                                                </ListItemContent>
                                            </Box>
                                            <List>
                                                {state.components.length === 0 && !state.isComponentsLoading && (
                                                    <p>{translate('components.overview.components.noResults')}</p>
                                                )}
                                                {debouncedSearchValue.length > 0 && state.components.length > 0 && (
                                                    <List.Item subtext=" ">
                                                        <span style={{ width: '100%', display: 'inline-block' }}>
                                                            {translate('components.overview.components.results', {
                                                                count: state.componentsTotal,
                                                            })}
                                                        </span>
                                                    </List.Item>
                                                )}
                                                <StyledCardGrid columnMinWidth={CARD_MIN_SIZE}>
                                                    {state.components.map((component) => (
                                                        <ComponentCard
                                                            key={component.id}
                                                            component={component}
                                                            collectionName={state.selectedCollection!.name}
                                                            onClick={(isUniqueContentProperties) => {
                                                                onApply(component, isUniqueContentProperties);
                                                            }}
                                                            onEdit={() => onComponentEdit(component)}
                                                            onDelete={() =>
                                                                onDeleteModalOpen(component.id, 'component')
                                                            }
                                                            isDisabled={isImageDesign && component.type === 'VIDEO'}
                                                        />
                                                    ))}
                                                </StyledCardGrid>
                                                <DelayedLoader isLoading={state.isComponentsLoading}>
                                                    <Flex justifyContent="center">
                                                        <Button
                                                            isLoading
                                                            variant="clean"
                                                            aria-label={translate('pages.loading')}
                                                        />
                                                    </Flex>
                                                </DelayedLoader>
                                                {state.components.length > 0 && (
                                                    <div
                                                        ref={lastComponentElementRef}
                                                        style={{ height: 1, width: '100%' }}
                                                    />
                                                )}
                                            </List>
                                        </>
                                    )}
                                </List>
                            </Box>
                        </ScrollableListContainer>
                    </ModalContentWrapper>
                </ModalBase.Content>
            </ModalBase>
            {renderCollectionEditModal()}
            {renderDeleteModal()}
        </>
    );
};

export default ComponentOverview;
